/**
 * Hook to fetch Blog Rich Text links (embedded entries/assets) separately.
 * This avoids query complexity issues by not including links in main page query.
 *
 * When one embedded entry is draft, Contentful may return partial data (published links only).
 * ContentfulService returns that partial data so published embeds still render;
 * the draft embed is skipped in rich-text render (missing from entryMap).
 */

import {useQuery} from '@tanstack/react-query';
import {print} from 'graphql';
import gql from 'graphql-tag';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {BaseVideoFragment} from '@amblique/contentful/app/contentful/fragments/video/base-video.fragment';

const BLOG_LINKS_QUERY = gql`
  ${BaseVideoFragment}
  query BlogLinks($id: String!, $locale: String, $preview: Boolean = false) {
    blog(id: $id, locale: $locale, preview: $preview) {
      blogContent {
        links {
          entries {
            block {
              __typename
              sys {
                id
              }
              ... on Video {
                ...BaseVideoFragment
              }
            }
          }
          assets {
            block {
              sys {
                id
              }
              contentType
              description
              fileName
              height
              width
              size
              title
              url
            }
          }
        }
      }
    }
  }
`;

/**
 * Fetch links for a Blog's blogContent field separately
 * @param {string} id - Blog sys.id
 * @param {Object} options - Query options
 * @returns {Object} - { links, isLoading, error, hasError }
 */
export const useBlogLinks = (id, options = {}) => {
  const contentfulService = useService(ContentfulService);

  const {data, isLoading, error, isError} = useQuery({
    queryKey: [
      contentfulService?.key,
      'blogLinks',
      id,
      contentfulService?.locale
    ],

    queryFn: async () => {
      if (!id || !contentfulService) return null;

      const response = await contentfulService.query(
        print(BLOG_LINKS_QUERY),
        {
          id,
          locale: contentfulService.locale,
          // Use preview mode to fetch links even if some entries are unpublished
          // This prevents the entire query from failing due to one unpublished entry
          preview: true,
        }
      );

      return response?.blog?.blogContent?.links || null;
    },

    enabled: !!id && !!contentfulService && options.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,

    // Don't retry on errors - just skip embedded entries
    retry: false,
  });

  // Log error but don't break - content will render without embedded entries
  if (isError && error) {
    console.warn(`[useBlogLinks] Failed to load links for ${id}:`, error.message);
  }

  return {
    links: data,
    isLoading,
    error,
    hasError: isError,
  };
};

export default useBlogLinks;
